<script setup lang="ts" generic="T extends { duid: string; order: string; title: string }">
import { computed, ref } from "vue";
import type { ComponentExposed } from "vue-component-type-helpers";

import Button from "~/components/dumb/Button.vue";
import DragArea from "~/components/dumb/DragArea.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import UpgradeConfirmationDialog from "~/components/dumb/UpgradeConfirmationDialog.vue";
import { PlusIcon } from "~/icons";
import { ButtonSize, ButtonStyle, IconSize, type Subscription } from "~/shared/enums";
import type { Constructable } from "~/shared/typeUtils";
import { getItemCountText } from "~/utils/common";

const props = defineProps<{
  title: string;
  items: T[];
  itemComponent: Constructable;
  getComponentProps: (item: T, index: number) => Record<string, unknown>;
  onCreate?: () => void;
  onChange?: (category: string, item: T) => void;
  dragGroup?: string;
  dragCategory?: string;
  maxItems?: number;
  upgradeSubscription?: Subscription;
  upgradeFeatureAction?: string;
}>();

const dragAreaRef = ref<ComponentExposed<typeof DragArea> | null>(null);

const upgradeRequired = computed(() => {
  if (!props.maxItems) {
    return false;
  }
  return props.items.length >= props.maxItems;
});

const singularTitle = computed(() => props.title.toLowerCase().slice(0, -1));

defineExpose({
  itemRefs: computed(() => dragAreaRef.value?.itemRefs ?? []),
  reset: () => dragAreaRef.value?.reset(),
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center justify-between">
      <span class="text-sm text-lt select-none">{{ title }}</span>
      <UpgradeConfirmationDialog
        v-if="onCreate && upgradeSubscription"
        :disabled="!upgradeRequired"
        :feature-action="
          upgradeFeatureAction ?? `create more than ${getItemCountText(maxItems ?? 0, 'item', { noSpecial: true })}`
        "
        :subscription="upgradeSubscription">
        <Tooltip v-if="items.length > 0" :text="`Create a ${singularTitle}`">
          <Button
            :btn-style="ButtonStyle.SECONDARY"
            :icon="PlusIcon"
            :a11y-label="`New ${singularTitle}`"
            :icon-args="{ class: 'text-lt' }"
            borderless
            class="!p-px hover:bg-lt"
            :icon-size="IconSize.S"
            :size="ButtonSize.SMALL"
            :disabled="upgradeRequired"
            @click="onCreate" />
        </Tooltip>
      </UpgradeConfirmationDialog>
      <Tooltip v-else-if="onCreate" :text="`Create a ${singularTitle}`">
        <span class="cursor-pointer rounded-sm p-0.5 hover:bg-md" @click="onCreate" @keydown.enter="onCreate">
          <PlusIcon class="icon-sm text-lt" />
        </span>
      </Tooltip>
    </div>

    <DragArea
      ref="dragAreaRef"
      :group="dragGroup ?? 'items'"
      :category="dragCategory ?? 'items'"
      class="-ml-6 !h-auto min-h-5 w-[calc(100%+1.5rem)]"
      :items="items"
      :component="itemComponent"
      :get-component-props="getComponentProps"
      drag-handle
      @change="(category, item) => onChange?.(category, item)" />
  </div>
</template>
